import { db } from '../firebase';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  limit,
  serverTimestamp
} from 'firebase/firestore';

const LOG_COLLECTION = 'E2B_SYSTEM_LOGS';
const MEMO_COLLECTION = 'E2B_DEV_MEMOS';

// 🚀 감사 로그에 남길 '수행 작업자'(등록된 이름). 로그인할 때 App/Login 이 채워 넣는다.
//    이렇게 한 곳에 보관해 두면 서비스마다 사용자 정보를 넘겨받지 않아도 된다.
let currentOperator = '';

export const logService = {
  // --- [1. 감사 로그(행위 이력) 관리] ---

  /** 로그인한 사람의 등록된 이름을 기록용으로 보관한다(로그아웃 시 빈 값). */
  setOperator: (name) => { currentOperator = name || ''; },

  /**
   * 행위 이력 한 줄을 남긴다.
   * ⚠️ 기록 실패가 본 작업(저장·삭제)을 절대 막으면 안 되므로,
   *    기다리지 않고(fire-and-forget) 오류도 콘솔에만 남긴다.
   * @param {{module:string, action:string, target?:string, details?:string, operator?:string}} entry
   */
  record: ({ module, action, target = '', details = '', operator } = {}) => {
    try {
      addDoc(collection(db, LOG_COLLECTION), {
        module: module || '기타',
        action: action || 'UPDATE',
        operator: operator || currentOperator || '알수없음',
        target: String(target || ''),
        details: String(details || ''),
        at: new Date().toISOString(),
        createdAt: serverTimestamp()
      }).catch(e => console.error('감사 로그 기록 실패:', e));
    } catch (e) {
      console.error('감사 로그 기록 실패:', e);
    }
  },

  /** 최근 행위 이력을 최신순으로 가져온다(기본 200건). */
  getLogs: async (max = 200) => {
    try {
      const q = query(collection(db, LOG_COLLECTION), orderBy('createdAt', 'desc'), limit(max));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (e) {
      console.error("로그 로드 실패:", e);
      return [];
    }
  },

  // --- [2. 개발 메모 및 파일 관리 (클라우드)] ---
  getMemos: async () => {
    try {
      const q = query(collection(db, MEMO_COLLECTION), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (e) {
      console.error("메모 로드 실패:", e);
      return [];
    }
  },
  
  // 메모 저장 및 파일 업로드 핵심 로직
  saveMemo: async (memoData, isEdit = false, originalId = null) => {
    try {
      if (isEdit && originalId) {
        // [수정 모드]
        const memoRef = doc(db, MEMO_COLLECTION, originalId);
        const memos = await logService.getMemos();
        const old = memos.find(m => m.id === originalId);
        
        // 버전 자동 계산 (입력값 없으면 0.1 증가)
        let nextVersion = memoData.version || (parseFloat(old.version || 1.0) + 0.1).toFixed(1);

        // 이전 기록 보관 (리비전)
        const newRevision = {
          version: old.version || "1.0",
          content: old.content || "",
          fileName: old.fileName || "",
          fileData: old.fileData || null,
          updatedAt: old.updatedAt || old.createdAt || new Date().toLocaleString()
        };
        
        await updateDoc(memoRef, {
          ...memoData,
          version: nextVersion.toString(),
          updatedAt: new Date().toLocaleString(),
          revisions: [newRevision, ...(old.revisions || [])]
        });
      } else {
        // [신규 등록 모드]
        await addDoc(collection(db, MEMO_COLLECTION), {
          title: memoData.title || "",
          content: memoData.content || "",
          version: memoData.version || "1.0",
          fileName: memoData.fileName || "", // 파일 이름 저장
          fileData: memoData.fileData || null, // 파일 본문(Base64) 저장
          createdAt: new Date().toLocaleString(),
          revisions: []
        });
      }
    } catch (e) {
      console.error("메모 저장 실패:", e);
      throw e;
    }
  },

  deleteMemo: async (id) => {
    try {
      await deleteDoc(doc(db, MEMO_COLLECTION, id));
    } catch (e) {
      console.error("메모 삭제 실패:", e);
    }
  }
};